<template>
  <div class="heartline-home">
    <app-navigation />

    <!-- Demo Request Modal -->
    <v-dialog v-model="demoFormVisible" max-width="600px" persistent>
      <v-card class="demo-form-card">
        <v-card-title class="demo-form-header">
          <div class="demo-form-title">
            <v-icon color="white" size="30" class="mr-3">mdi-calendar-check</v-icon>
            <span>Request a Demo</span>
          </div>
          <v-btn icon dark @click="closeDemoForm">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-6">
          <v-alert v-if="formSuccess" type="success" class="mb-4" outlined>
            Thanks! Someone from our team will reach out within one business day.
          </v-alert>
          <v-alert v-if="formError" type="error" class="mb-4" outlined>
            {{ formError }}
          </v-alert>

          <v-form v-if="!formSuccess" ref="demoForm" v-model="formValid">
            <v-text-field v-model="demoFormData.name" label="Your name" outlined dense class="hh-field mb-2" :rules="[rules.required]" />
            <v-text-field v-model="demoFormData.email" label="Email" outlined dense class="hh-field mb-2" :rules="[rules.required, rules.email]" />
            <v-text-field v-model="demoFormData.phone" label="Phone (optional)" outlined dense class="hh-field mb-2" />
            <v-text-field v-model="demoFormData.communityName" label="Community name" outlined dense class="hh-field mb-2" :rules="[rules.required]" />
            <v-select v-model="demoFormData.communitySize" :items="communitySizeOptions" label="Community size" outlined dense class="hh-field mb-2" :rules="[rules.required]" />
            <v-textarea v-model="demoFormData.message" label="Anything specific you'd like us to know? (optional)" outlined dense rows="3" class="hh-field" />
          </v-form>
        </v-card-text>

        <v-card-actions v-if="!formSuccess" class="px-6 pb-6">
          <v-spacer />
          <v-btn text @click="closeDemoForm">Cancel</v-btn>
          <v-btn class="hh-primary-btn" :loading="formSubmitting" @click="submitDemoRequest">
            Send Request
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== HERO ==================== -->
    <section class="hero-section">
      <div class="hero-background">
        <div class="hero-orb hero-orb-1"></div>
        <div class="hero-orb hero-orb-2"></div>
        <div class="hero-rings"></div>
      </div>

      <v-container class="hero-content py-16">
        <v-row align="center">
          <v-col cols="12" md="6">
            <div class="hero-eyebrow mb-5" :class="{ 'fade-in-up': mounted }">
              <v-icon x-small color="#E4C989" class="mr-2">mdi-image-multiple</v-icon>
              RESIDENTS · FAMILIES · STAFF · LEADERSHIP
            </div>
            <h1 class="hero-title mb-6" :class="{ 'slide-in-left': mounted }">
              Keep Every Family Close.<br />Keep Every Resident Known.
            </h1>
            <p class="hero-subtitle mb-8" :class="{ 'slide-in-left-delayed': mounted }">
              Heartline Hub is a private social platform built for senior living communities — share
              memories and celebrations, keep families updated on daily care, coordinate dining and
              activities, and give every role, from resident to leadership, the tools they actually need.
            </p>
            <div class="hero-actions mb-8" :class="{ 'fade-in-up-delayed': mounted }">
              <v-btn large class="hh-primary-btn mr-4" @click="openDemoForm">
                <v-icon left small>mdi-rocket-launch</v-icon>
                Get Started Free
              </v-btn>
              <v-btn large text class="hero-link-btn" @click="scrollToFeatures">
                See What's Inside
                <v-icon right small>mdi-arrow-down</v-icon>
              </v-btn>
            </div>
            <div class="hero-trust" :class="{ 'fade-in-up-delayed': mounted }">
              <span class="hero-trust-item"><v-icon x-small color="#E4C989" class="mr-1">mdi-shield-check</v-icon>AES-256 Encrypted</span>
              <span class="hero-trust-divider">•</span>
              <span class="hero-trust-item"><v-icon x-small color="#E4C989" class="mr-1">mdi-clock-check-outline</v-icon>99.9% Uptime</span>
            </div>
          </v-col>

          <v-col cols="12" md="6" class="d-none d-md-flex justify-center">
            <div class="phone-mockup" :class="{ 'phone-in': mounted }">
              <div class="phone-frame">
                <div class="phone-notch"></div>
                <div class="phone-screen">
                  <div class="mock-topbar">
                    <v-icon x-small color="#FBF7EF">mdi-heart</v-icon>
                    <span>Heartline Hub</span>
                    <v-icon x-small color="#FBF7EF">mdi-bell-outline</v-icon>
                  </div>
                  <div class="mock-tiles">
                    <div class="mock-tile" v-for="t in mockTiles" :key="t.label">
                      <div class="mock-tile-icon" :style="{ background: t.tint }">
                        <v-icon x-small :color="t.iconColor">{{ t.icon }}</v-icon>
                      </div>
                      <span>{{ t.label }}</span>
                    </div>
                  </div>
                  <div class="mock-post">
                    <div class="mock-post-hdr">
                      <div class="mock-avatar"></div>
                      <div>
                        <div class="mock-post-name">Eleanor's Garden Day</div>
                        <div class="mock-post-time">2 hours ago</div>
                      </div>
                    </div>
                    <p class="mock-post-text">Had a wonderful afternoon planting with the garden club! 🌷</p>
                    <div class="mock-post-media"></div>
                    <div class="mock-post-actions">
                      <span><v-icon x-small color="#B3554A">mdi-heart</v-icon> 18</span>
                      <span><v-icon x-small color="#5B6663">mdi-comment-outline</v-icon> 6</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ==================== FEATURES ==================== -->
    <section id="features" class="features-section">
      <v-container class="py-16">
        <div class="section-header text-center mb-12">
          <div class="section-eyebrow">EVERYTHING IN ONE PLACE</div>
          <h2 class="section-title">Built Around Real Community Life</h2>
          <p class="section-subtitle">
            Not a generic message board — every feature here reflects how a senior living community
            actually runs day to day.
          </p>
        </div>

        <v-row class="features-grid" :class="{ 'features-visible': featuresVisible }">
          <v-col v-for="(f, i) in features" :key="f.title" cols="12" sm="6" lg="4">
            <div class="feature-card" :style="{ transitionDelay: (i * 0.06) + 's' }">
              <div class="feature-icon" :style="{ background: f.tint }">
                <v-icon :color="f.iconColor">{{ f.icon }}</v-icon>
              </div>
              <h3 class="feature-title">{{ f.title }}</h3>
              <p class="feature-desc">{{ f.description }}</p>
              <ul class="feature-points">
                <li v-for="p in f.benefits" :key="p"><v-icon x-small color="#C89B4C">mdi-check</v-icon>{{ p }}</li>
              </ul>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ==================== INTERACTIVE DEMO ==================== -->
    <section id="demo" class="demo-section">
      <v-container class="py-16">
        <div class="section-header text-center mb-10">
          <div class="section-eyebrow">ONE PLATFORM, FIVE EXPERIENCES</div>
          <h2 class="section-title">A Dashboard for Every Role</h2>
          <p class="section-subtitle">Residents, family, caregivers, the kitchen and leadership each get exactly what their day needs, and see only what their role allows.</p>
        </div>

        <div class="demo-tabs">
          <button
            v-for="(tab, i) in demoTabs" :key="tab.label"
            class="demo-tab" :class="{ 'demo-tab-active': activeTab === i }"
            @click="activeTab = i"
          >
            <v-icon small :color="activeTab === i ? '#1E3733' : '#9CA6A0'">{{ tab.icon }}</v-icon>
            {{ tab.label }}
          </button>
        </div>

        <div class="demo-panel" :class="{ 'demo-visible': demoVisible }">
          <div class="demo-panel-icon" :style="{ background: demoTabs[activeTab].tint }">
            <v-icon size="26" :color="demoTabs[activeTab].iconColor">{{ demoTabs[activeTab].icon }}</v-icon>
          </div>
          <h3 class="demo-panel-title">{{ demoTabs[activeTab].title }}</h3>
          <p class="demo-panel-desc">{{ demoTabs[activeTab].description }}</p>
          <div class="demo-panel-items">
            <div v-for="item in demoTabs[activeTab].items" :key="item" class="demo-panel-item">
              <v-icon x-small color="#C89B4C">mdi-check-circle-outline</v-icon>{{ item }}
            </div>
          </div>
        </div>

        <div class="platform-stats mt-14">
          <div v-for="s in platformStats" :key="s.label" class="stat-card">
            <div class="stat-value">{{ s.value }}</div>
            <div class="stat-label">{{ s.label }}</div>
          </div>
        </div>
      </v-container>
    </section>

    <!-- ==================== PRIVATE BY DESIGN ==================== -->
    <section id="privacy" class="privacy-section">
      <v-container class="py-16">
        <div class="section-header text-center mb-12">
          <div class="section-eyebrow">PRIVATE BY DESIGN</div>
          <h2 class="section-title">The Right People See the Right Things</h2>
          <p class="section-subtitle">
            A care update about Mom reaches her family, not every family in the building. Access
            is enforced by the platform itself, so it never depends on someone remembering a setting.
          </p>
        </div>

        <v-row>
          <v-col v-for="w in whoSeesWhat" :key="w.title" cols="12" sm="6" lg="3">
            <div class="privacy-card">
              <div class="privacy-icon"><v-icon color="#2F4F4A">{{ w.icon }}</v-icon></div>
              <h3 class="privacy-title">{{ w.title }}</h3>
              <div class="privacy-label">Who can see it</div>
              <p class="privacy-who">{{ w.who }}</p>
              <p class="privacy-note">{{ w.note }}</p>
            </div>
          </v-col>
        </v-row>

        <div class="privacy-strip mt-10">
          <div v-for="s in privacyPoints" :key="s" class="privacy-strip-item">
            <v-icon small color="#C89B4C" class="mr-2">mdi-shield-check-outline</v-icon>{{ s }}
          </div>
        </div>
      </v-container>
    </section>

    <!-- ==================== BENEFITS ==================== -->
    <section class="benefits-section">
      <v-container class="py-16">
        <div class="section-header text-center mb-12">
          <div class="section-eyebrow section-eyebrow-light">WHY IT WORKS</div>
          <h2 class="section-title section-title-light">Something for Everyone in the Community</h2>
        </div>

        <v-row :class="{ 'benefits-visible': benefitsVisible }">
          <v-col v-for="(b, i) in coreBenefits" :key="b.badge" cols="12" md="6">
            <div class="benefit-card" :style="{ transitionDelay: (i * 0.08) + 's' }">
              <div class="benefit-icon-section">
                <v-icon color="#E4C989">{{ b.icon }}</v-icon>
              </div>
              <div class="benefit-content-section">
                <span class="benefit-badge">{{ b.badge }}</span>
                <h3 class="benefit-title">{{ b.title }}</h3>
                <p class="benefit-desc">{{ b.description }}</p>
                <ul class="benefit-points">
                  <li v-for="p in b.points" :key="p"><v-icon x-small color="#E4C989">mdi-check</v-icon>{{ p }}</li>
                </ul>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ==================== TECHNOLOGY ==================== -->
    <section class="technology-section">
      <v-container class="py-16">
        <div class="section-header text-center mb-12">
          <div class="section-eyebrow">UNDER THE HOOD</div>
          <h2 class="section-title">Secure, Reliable, Always On</h2>
        </div>

        <v-row>
          <v-col v-for="t in techFeatures" :key="t.title" cols="6" sm="4" md="2">
            <div class="tech-card">
              <v-icon size="26" :color="t.color">{{ t.icon }}</v-icon>
              <h4 class="tech-title">{{ t.title }}</h4>
              <p class="tech-desc">{{ t.description }}</p>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ==================== TRIBZZ ==================== -->
    <section id="tribzz" class="tribzz-band">
      <v-container class="py-16">
        <v-row align="center">
          <v-col cols="12" md="6">
            <div class="tz-eyebrow">FREE FOR YOUR COMMUNITY · ANYONE CAN START ONE</div>
            <div class="tz-wordmark">TRIBZZ</div>
            <div class="tz-tagline">◆ YOU CREATE THE CULTURE ◆</div>
            <h2 class="tz-title">Show families what life at your community really feels like.</h2>
            <p class="tz-text">
              Heartline Hub keeps your residents and families connected on the inside. A free
              Tribzz on <strong>tribzz.com</strong> is your community's public front porch: activities,
              celebrations, open houses and live performances that prospective residents and their
              adult children can follow before they ever book a tour.
            </p>
            <div class="tz-actions">
              <v-btn large class="tz-btn" to="/tribzz">
                How Tribzz helps you welcome new residents
                <v-icon right small>mdi-arrow-right</v-icon>
              </v-btn>
              <v-btn large text class="tz-link" href="https://tribzz.com" target="_blank" rel="noopener">
                Visit tribzz.com
                <v-icon right small>mdi-open-in-new</v-icon>
              </v-btn>
            </div>
          </v-col>
          <v-col cols="12" md="6">
            <div class="tz-card">
              <div class="tz-card-cover">
                <span class="tz-card-letter">M</span>
              </div>
              <div class="tz-card-body">
                <div class="tz-card-kicker">A PLACE · PEOPLE CHECK IN</div>
                <div class="tz-card-name">Maple Grove Senior Living</div>
                <div class="tz-card-desc">Garden club, Friday concerts, and the best Sunday brunch in town.</div>
                <div class="tz-card-chips">
                  <span v-for="c in tribzzChips" :key="c">{{ c }}</span>
                </div>
                <div class="tz-card-post">
                  <span class="tz-quote">“🎻 Live: Friday string quartet in the courtyard”</span>
                  <span class="tz-here">● 14 here now</span>
                </div>
                <div class="tz-card-join">JOIN</div>
              </div>
            </div>
            <div class="tz-card-caption">An example of how a community's Tribzz could look.</div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- ==================== PRICING ==================== -->
    <section id="pricing" class="pricing-section">
      <v-container class="py-16">
        <div class="section-header text-center mb-12">
          <div class="section-eyebrow">SIMPLE, TRANSPARENT PRICING</div>
          <h2 class="section-title">One Plan. Everything Included.</h2>
        </div>

        <div class="pricing-card" :class="{ 'pricing-visible': pricingVisible }">
          <div class="pricing-top">
            <div class="pricing-price"><span class="price-num">$299.99</span><span class="price-period">/month</span></div>
            <div class="price-note">per community · volume pricing available for multi-home operators</div>
          </div>

          <v-row class="mt-8">
            <v-col v-for="g in featureGroups" :key="g.title" cols="12" sm="6" lg="4">
              <div class="pricing-group">
                <div class="pricing-group-hdr">
                  <v-icon small color="#C89B4C" class="mr-2">{{ g.icon }}</v-icon>
                  <span>{{ g.title }}</span>
                </div>
                <div v-for="f in g.features" :key="f.name" class="pricing-feature">
                  <v-icon x-small color="#3F7A5C" class="mr-2 mt-1">mdi-check</v-icon>
                  <div>
                    <div class="pf-name">{{ f.name }}</div>
                    <div class="pf-detail">{{ f.detail }}</div>
                  </div>
                </div>
              </div>
            </v-col>
          </v-row>

          <div class="text-center mt-8">
            <v-btn large class="hh-primary-btn" @click="openDemoForm">
              <v-icon left small>mdi-calendar-check</v-icon>
              Request a Demo
            </v-btn>
          </div>
        </div>
      </v-container>
    </section>

    <!-- ==================== FINAL CTA ==================== -->
    <section class="final-cta-section">
      <v-container class="py-16 text-center">
        <h2 class="final-cta-title mb-4">Ready to Bring Your Community Closer Together?</h2>
        <p class="final-cta-subtitle mb-8">
          See how Heartline Hub fits your community's day-to-day — we'll walk you through it live.
        </p>
        <v-btn x-large class="hh-primary-btn" @click="openDemoForm">
          <v-icon left>mdi-rocket-launch</v-icon>
          Get Started Free
        </v-btn>
      </v-container>
    </section>

    <app-footer />
  </div>
</template>

<script>
import { db } from '@/firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import AppNavigation from '@/components/AppNavigation.vue';
import AppFooter from '@/components/AppFooter.vue';

export default {
  name: 'HeartlineHome',
  components: { AppNavigation, AppFooter },

  data: () => ({
    mounted: false,
    featuresVisible: false,
    demoVisible: false,
    benefitsVisible: false,
    pricingVisible: false,
    activeTab: 0,

    // Demo Form — writes to the same `contact_requests` collection the
    // original Seniorz Connect form used, so any existing lead-review
    // tooling pointed at that collection keeps working unchanged.
    demoFormVisible: false,
    formValid: false,
    formSubmitting: false,
    formError: '',
    formSuccess: false,
    demoFormData: {
      name: '', email: '', phone: '', communityName: '', communitySize: '', message: '',
    },
    communitySizeOptions: ['1-25 residents', '26-50 residents', '51-100 residents', '101-200 residents', '201+ residents'],
    rules: {
      required: (value) => !!value || 'This field is required',
      email: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || 'Invalid email address',
    },

    mockTiles: [
      { label: 'Dining', icon: 'mdi-food-variant', tint: '#EFE6D2', iconColor: '#8A6A2B' },
      { label: 'Activities', icon: 'mdi-calendar-star', tint: '#E3ECE9', iconColor: '#2F4F4A' },
      { label: 'Spotlight', icon: 'mdi-star-outline', tint: '#F5EAD2', iconColor: '#B3862E' },
    ],

    // Every card below describes something actually built and working —
    // not aspirational copy.
    features: [
      {
        icon: 'mdi-image-multiple-outline', tint: '#E6E9F0', iconColor: '#43507A',
        title: 'Explore Feed & Memory Albums',
        description: 'Multi-photo and video posts with captions, collaborative albums families can add to, and a moderated community feed everyone can browse.',
        benefits: ['Multiple photos & videos per post', 'Collaborative, caption-ready albums', 'Public and connections-only feeds'],
      },
      {
        icon: 'mdi-clipboard-pulse-outline', tint: '#F3E3DE', iconColor: '#B3554A',
        title: 'Care Updates',
        description: 'Staff post how each resident is doing. Only the family members linked to that resident see it, the moment it\'s posted, and caregivers can update several residents at once during rounds.',
        benefits: ['Doing well / needs attention / unwell', 'One-tap or bulk posting for staff', 'Push notification to linked family only'],
      },
      {
        icon: 'mdi-star-outline', tint: '#F5EAD2', iconColor: '#B3862E',
        title: 'Spotlight & Celebrations',
        description: 'Birthdays, anniversaries, staff shoutouts, and resident-of-the-month honors — with a celebrate reaction so the whole community can join in.',
        benefits: ['Birthdays, anniversaries & honors', 'Photos attached to every celebration', 'Community "celebrate" reactions'],
      },
      {
        icon: 'mdi-account-group', tint: '#E3ECE9', iconColor: '#2F4F4A',
        title: 'Groups & Clubs',
        description: 'Public or private interest groups — book clubs, garden clubs, family circles — each with their own feed and multi-media posts.',
        benefits: ['Public and private groups', 'Group-specific media feeds', 'Simple join & member management'],
      },
      {
        icon: 'mdi-message-text-outline', tint: '#E6E9F0', iconColor: '#43507A',
        title: 'Messaging & Directory',
        description: 'A real-time chat with photo and file attachments, plus a role-aware directory so anyone can find and message residents, family, or staff.',
        benefits: ['Text, photos & file attachments', 'Directory filtered by role', 'Alerts on phone or tablet, even when closed'],
      },
      {
        icon: 'mdi-calendar-month-outline', tint: '#E3ECE9', iconColor: '#2F4F4A',
        title: 'Community Calendar',
        description: 'Month, agenda, and weekly-at-a-glance views, color-coded by category, with recurring activities and RSVP built in.',
        benefits: ['Recurring weekly activities', 'One-tap RSVP with capacity limits', 'Wellness, dining, and outings, color-coded'],
      },
      {
        icon: 'mdi-food-variant', tint: '#EFE6D2', iconColor: '#8A6A2B',
        title: 'Dining Menu',
        description: 'From morning coffee through the evening social hour — every meal period, dietary tags included, managed directly by kitchen staff.',
        benefits: ['8 daily meal & social periods', 'Dietary tags on every item', 'Kitchen staff post directly'],
      },
      {
        icon: 'mdi-bullhorn-outline', tint: '#F3E3DE', iconColor: '#B3554A',
        title: 'Announcements & Resources',
        description: 'Audience-targeted announcements — residents, family, staff, or everyone — plus a searchable resource library organized by category.',
        benefits: ['Targeted by audience, not blasted', 'Pin featured announcements', 'Resource library by category'],
      },
      {
        icon: 'mdi-shield-check-outline', tint: '#F5EAD2', iconColor: '#8A6A2B',
        title: 'Moderation & Admin Tools',
        description: 'Flaggable content with a real review queue, role-based dashboards for staff versus leadership, and full user & group management.',
        benefits: ['Report & review queue for posts', 'Separate staff and leadership views', 'Full resident/family linking tools'],
      },
      {
        icon: 'mdi-shield-lock-outline', tint: '#E3ECE9', iconColor: '#2F4F4A',
        title: 'Private by Design',
        description: 'Phone numbers, room numbers, care levels and dietary needs live in a private profile only the resident, the care team and linked family can open. The community directory shows names, photos and interests.',
        benefits: ['Private profiles, public directory', 'Residents choose whether to share contact details', 'Roles can only be changed by your admins'],
      },
      {
        icon: 'mdi-bell-ring-outline', tint: '#F5EAD2', iconColor: '#B3862E',
        title: 'Notifications on Any Device',
        description: 'Messages, care updates, events and announcements arrive as notifications on phones, tablets and computers, even when the app isn\'t open. Each person turns them on for their own device.',
        benefits: ['Works in the browser and the apps', 'Signing out removes the device', 'Safe on a shared community tablet'],
      },
      {
        icon: 'mdi-format-size', tint: '#E6E9F0', iconColor: '#43507A',
        title: 'Made for Older Eyes',
        description: 'A Text size setting (Standard, Large or Extra large) scales every screen. Residents and families start on Large, and the choice follows them to any device they sign in on.',
        benefits: ['Three text sizes, saved to the account', 'Screen-reader labels on every button', 'Full keyboard support and reduced motion'],
      },
    ],

    demoTabs: [
      {
        icon: 'mdi-account-heart-outline', label: 'Resident',
        title: 'A Simple, Warm Home Screen',
        description: 'Large, legible tiles for connections, groups, the explore feed, and community life, with a Text size setting that starts on Large.',
        tint: '#E3ECE9', iconColor: '#2F4F4A',
        items: ['One-tap access to Dining, Calendar & Spotlight', 'Make new connections with neighbors', 'Browse and post to the community feed'],
      },
      {
        icon: 'mdi-account-multiple-outline', label: 'Family',
        title: 'Follow Every Resident You Love',
        description: 'Switch between multiple residents you follow, see their live care status, and jump straight into dining, activities, or a message.',
        tint: '#E6E9F0', iconColor: '#43507A',
        items: ['Real-time care status, not a static note', 'Follow more than one resident at once', 'Sees only the residents they\'re linked to'],
      },
      {
        icon: 'mdi-badge-account-outline', label: 'Staff',
        title: 'Built for Actual Shift Work',
        description: 'A resident roster with one-tap status buttons, and a bulk mode for posting the same update to many residents during rounds at once.',
        tint: '#F3E3DE', iconColor: '#B3554A',
        items: ['Bulk care-update posting', 'Full resident roster at a glance', 'Direct messaging to any family member'],
      },
      {
        icon: 'mdi-food-variant', label: 'Kitchen',
        title: 'The Menu, Straight From the Kitchen',
        description: 'Kitchen staff publish every meal period themselves, from morning coffee to the evening social hour, with dietary tags residents and families can trust.',
        tint: '#EFE6D2', iconColor: '#8A6A2B',
        items: ['8 meal & social periods a day', 'Dietary tags like low-sodium, pureed & diabetic-friendly', 'Can post care updates and message residents'],
      },
      {
        icon: 'mdi-shield-crown-outline', label: 'Leadership',
        title: 'Run the Whole Community',
        description: 'User management, resident-family linking, content moderation, and community-wide announcements — all from one dashboard.',
        tint: '#F5EAD2', iconColor: '#8A6A2B',
        items: ['Add people and they get an email to set their own password', 'Link families to the residents they follow', 'Review flagged content & post announcements'],
      },
    ],

    whoSeesWhat: [
      { icon: 'mdi-clipboard-pulse-outline', title: 'Care updates', who: 'The resident, the care team, and family linked to that resident.', note: 'Other families never see them, even by accident.' },
      { icon: 'mdi-card-account-details-outline', title: 'Phone, room & care level', who: 'The resident, the care team, and linked family.', note: 'Residents can choose to share their phone and email with neighbors.' },
      { icon: 'mdi-message-lock-outline', title: 'Messages', who: 'Only the two people in the conversation.', note: 'Other residents, families and staff can\'t read your conversations.' },
      { icon: 'mdi-account-group-outline', title: 'Community directory', who: 'Everyone in your community.', note: 'Names, photos, bios and interests, so neighbors can connect.' },
    ],
    privacyPoints: [
      'Roles are set by your admins, never by the person themselves',
      'Admins add people; each person sets their own password by email',
      'Signing out on a shared tablet stops that person\'s alerts',
    ],
    tribzzChips: ['EVENTS', 'HERE NOW', 'LIVE', 'PHOTOS', 'PARTNERS WALL'],

    platformStats: [
      { value: '<100ms', label: 'Average Load Time' },
      { value: 'Unlimited', label: 'Photo & Video Storage' },
      { value: 'Unlimited', label: 'User Accounts' },
      { value: 'Quick', label: 'Support Response' },
    ],

    coreBenefits: [
      {
        icon: 'mdi-account-heart-outline', badge: 'FOR RESIDENTS',
        title: 'Connect & Belong',
        description: 'Simple, accessible tools to stay connected with family and take part in community life.',
        points: ['Text size that starts on Large', 'See family photos and messages instantly', 'Browse activities and dining at a glance'],
      },
      {
        icon: 'mdi-account-multiple-outline', badge: 'FOR FAMILY',
        title: 'Stay Close From Anywhere',
        description: 'Real-time care updates and community life, without needing to call the front desk.',
        points: ['Live wellness status, not guesswork', 'A notification the moment there\'s an update', 'Direct messaging to residents and staff'],
      },
      {
        icon: 'mdi-badge-account-outline', badge: 'FOR STAFF',
        title: 'Simplify Daily Care',
        description: 'Tools that actually fit a shift, not extra paperwork on top of it.',
        points: ['Bulk-post updates during rounds', 'One directory for every resident and family', 'Dining menu management built in'],
      },
      {
        icon: 'mdi-shield-crown-outline', badge: 'FOR LEADERSHIP',
        title: 'Run With Confidence',
        description: 'Full oversight of your community without living in spreadsheets.',
        points: ['Complete user & resident-family linking', 'Real moderation, not an honor system', 'One dashboard for the whole community'],
      },
    ],

    techFeatures: [
      { icon: 'mdi-security', color: '#B3554A', title: 'Bank-Level Security', description: 'AES-256 encryption on all data.' },
      { icon: 'mdi-cloud-lock-outline', color: '#43507A', title: 'Secure Cloud', description: '99.9% uptime guarantee.' },
      { icon: 'mdi-backup-restore', color: '#3F7A5C', title: 'Auto Backups', description: 'Automated daily backups.' },
      { icon: 'mdi-update', color: '#8A6A2B', title: 'Regular Updates', description: 'Continuous improvements.' },
      { icon: 'mdi-cellphone-link', color: '#2F4F4A', title: 'Cross-Platform', description: 'Works on any device.' },
      { icon: 'mdi-face-agent', color: '#B3862E', title: 'Real Support', description: 'A team that answers.' },
    ],

    featureGroups: [
      {
        icon: 'mdi-image-multiple-outline', title: 'Community & Recognition',
        features: [
          { name: 'Explore feed & memory albums', detail: 'Multi-photo & video posts, captions' },
          { name: 'Spotlight & celebrations', detail: 'Birthdays, anniversaries, shoutouts, honors' },
          { name: 'Groups & clubs', detail: 'Public & private, with media feeds' },
        ],
      },
      {
        icon: 'mdi-clipboard-pulse-outline', title: 'Care & Family Connection',
        features: [
          { name: 'Care updates', detail: 'Live status, single or bulk posting' },
          { name: 'Family dashboard', detail: 'Follow multiple residents at once' },
          { name: 'Resident-family linking', detail: 'Managed by your admin team' },
          { name: 'Push notifications', detail: 'Phones, tablets and browsers' },
        ],
      },
      {
        icon: 'mdi-calendar-month-outline', title: 'Daily Life & Dining',
        features: [
          { name: 'Community calendar', detail: 'Recurring activities, RSVP, weekly view' },
          { name: 'Dining menu', detail: '8 meal & social periods, dietary tags' },
        ],
      },
      {
        icon: 'mdi-message-text-outline', title: 'Communication',
        features: [
          { name: 'Role-based directory & messaging', detail: 'Photos, files, unread badges' },
          { name: 'Targeted announcements', detail: 'By audience, with pinning' },
          { name: 'Resource library', detail: 'Organized by category' },
        ],
      },
      {
        icon: 'mdi-cog-outline', title: 'Staff & Admin Tools',
        features: [
          { name: 'Staff dashboard', detail: 'Separate from leadership tools' },
          { name: 'Bulk care-update tools', detail: 'Built for real shift work' },
          { name: 'User & group management', detail: 'Full admin control panel' },
        ],
      },
      {
        icon: 'mdi-shield-check-outline', title: 'Security & Support',
        features: [
          { name: 'Enterprise security', detail: 'AES-256 encryption, SOC 2 Type II' },
          { name: 'Content moderation', detail: 'Report queue with real review' },
          { name: 'Private profiles', detail: 'Contact & care details stay private' },
          { name: 'Accessibility', detail: 'Text size setting, screen-reader labels' },
          { name: 'Free onboarding', detail: 'Setup assistance and training' },
          { name: 'SLA guarantee', detail: '99.9% uptime commitment' },
        ],
      },
    ],
  }),

  mounted() {
    setTimeout(() => { this.mounted = true; }, 100);
    this.setupScrollAnimations();
  },

  methods: {
    openDemoForm() {
      this.demoFormVisible = true;
      this.formError = '';
      this.formSuccess = false;
    },
    closeDemoForm() {
      this.demoFormVisible = false;
      this.resetForm();
    },
    resetForm() {
      this.demoFormData = { name: '', email: '', phone: '', communityName: '', communitySize: '', message: '' };
      this.formError = '';
      this.formSuccess = false;
      if (this.$refs.demoForm) this.$refs.demoForm.resetValidation();
    },
    async submitDemoRequest() {
      if (!this.$refs.demoForm.validate()) return;
      this.formSubmitting = true;
      this.formError = '';
      this.formSuccess = false;
      try {
        await addDoc(collection(db, 'contact_requests'), {
          name: this.demoFormData.name,
          email: this.demoFormData.email,
          phone: this.demoFormData.phone || '',
          communityName: this.demoFormData.communityName,
          communitySize: this.demoFormData.communitySize,
          message: this.demoFormData.message || '',
          createdAt: serverTimestamp(),
          read: false,
          status: 'new',
        });
        this.formSuccess = true;
        setTimeout(() => { this.closeDemoForm(); }, 3000);
      } catch (error) {
        console.error('Error submitting demo request:', error);
        this.formError = 'There was an error submitting your request. Please try again or contact us directly.';
      } finally {
        this.formSubmitting = false;
      }
    },

    scrollToFeatures() {
      document.getElementById('features')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },

    setupScrollAnimations() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (entry.target.classList.contains('features-section')) this.featuresVisible = true;
          else if (entry.target.classList.contains('demo-section')) this.demoVisible = true;
          else if (entry.target.classList.contains('benefits-section')) this.benefitsVisible = true;
          else if (entry.target.classList.contains('pricing-section')) this.pricingVisible = true;
        });
      }, { threshold: 0.15, rootMargin: '0px 0px -50px 0px' });

      setTimeout(() => {
        document.querySelectorAll('.features-section, .demo-section, .benefits-section, .pricing-section')
          .forEach((section) => observer.observe(section));
      }, 500);
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600;9..144,700&family=Inter:wght@400;500;600;700&display=swap');

* { box-sizing: border-box; }

.heartline-home {
  --rc-teal-deep: #1E3733;
  --rc-teal: #2F4F4A;
  --rc-gold: #C89B4C;
  --rc-gold-soft: #E4C989;
  --rc-cream: #FBF7EF;
  --rc-ink: #26302C;
  --rc-ink-soft: #5B6663;
  --rc-line: #E4DCC9;

  overflow-x: hidden;
  background: var(--rc-cream);
  padding-top: 78px;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: var(--rc-ink);
}

.hh-primary-btn.v-btn {
  background: linear-gradient(150deg, var(--rc-gold-soft) 0%, var(--rc-gold) 100%) !important;
  color: var(--rc-teal-deep) !important;
  font-weight: 700;
  text-transform: none;
  border-radius: 10px;
  box-shadow: 0 10px 24px rgba(200, 155, 76, 0.3) !important;
}

.hh-field >>> .v-input__slot { background: #fff !important; border-radius: 10px; }

/* ==================== SECTION HEADERS (shared) ==================== */
.section-eyebrow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: var(--rc-gold);
}
.section-eyebrow-light { color: var(--rc-gold-soft); }
.section-title {
  font-family: 'Fraunces', serif;
  font-weight: 500;
  font-size: 34px;
  color: var(--rc-ink);
  margin: 10px 0 12px;
}
.section-title-light { color: var(--rc-cream); }
.section-subtitle {
  font-size: 16px;
  color: var(--rc-ink-soft);
  max-width: 560px;
  margin: 0 auto;
}

/* ==================== HERO ==================== */
.hero-section {
  position: relative;
  background: linear-gradient(160deg, var(--rc-teal-deep) 0%, var(--rc-teal) 55%, #1a302c 100%);
  overflow: hidden;
  padding-bottom: 40px;
}
.hero-background { position: absolute; inset: 0; overflow: hidden; }
.hero-orb { position: absolute; border-radius: 50%; filter: blur(60px); opacity: 0.35; }
.hero-orb-1 { width: 420px; height: 420px; background: var(--rc-gold); top: -120px; left: -100px; }
.hero-orb-2 { width: 320px; height: 320px; background: #4A6FA5; bottom: -80px; right: -60px; opacity: 0.2; }
.hero-rings {
  position: absolute; inset: 0;
  background-image: repeating-radial-gradient(circle at 100% 100%, rgba(251,247,239,0.035) 0, rgba(251,247,239,0.035) 1px, transparent 1px, transparent 28px);
}
.hero-content { position: relative; z-index: 1; }

.hero-eyebrow {
  display: inline-flex; align-items: center;
  font-size: 12px; font-weight: 700; letter-spacing: 0.14em;
  color: var(--rc-gold-soft);
  background: rgba(251,247,239,0.08); border: 1px solid rgba(251,247,239,0.15);
  padding: 7px 16px; border-radius: 100px;
  opacity: 0; transform: translateY(10px); transition: all 0.6s ease;
}
.hero-title {
  font-family: 'Fraunces', serif; font-weight: 500;
  font-size: 46px; line-height: 1.15; color: var(--rc-cream);
  opacity: 0; transform: translateX(-20px); transition: all 0.7s ease;
}
.hero-subtitle {
  font-size: 17px; line-height: 1.7; color: rgba(251,247,239,0.75); max-width: 480px;
  opacity: 0; transform: translateX(-20px); transition: all 0.7s ease 0.15s;
}
.hero-actions, .hero-trust { opacity: 0; transform: translateY(10px); transition: all 0.6s ease 0.3s; }
.fade-in-up, .fade-in-up-delayed { opacity: 1 !important; transform: translateY(0) !important; }
.slide-in-left, .slide-in-left-delayed { opacity: 1 !important; transform: translateX(0) !important; }

.hero-link-btn.v-btn { color: var(--rc-gold-soft) !important; text-transform: none; font-weight: 600; }
.hero-trust { display: flex; align-items: center; gap: 10px; font-size: 13px; color: rgba(251,247,239,0.6); }
.hero-trust-divider { opacity: 0.4; }

.phone-mockup { opacity: 0; transform: translateY(30px) scale(0.96); transition: all 0.8s ease 0.3s; }
.phone-in { opacity: 1; transform: translateY(0) scale(1); }
.phone-frame {
  width: 300px; border-radius: 40px; background: #0f1a18; padding: 12px;
  box-shadow: 0 40px 80px rgba(0,0,0,0.4);
  position: relative;
}
.phone-notch { width: 110px; height: 22px; background: #0f1a18; border-radius: 0 0 14px 14px; position: absolute; top: 0; left: 50%; transform: translateX(-50%); z-index: 2; }
.phone-screen { background: var(--rc-cream); border-radius: 30px; overflow: hidden; padding-bottom: 10px; }
.mock-topbar {
  background: var(--rc-teal-deep); color: var(--rc-cream); font-family: 'Fraunces', serif; font-weight: 500;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 26px 16px 12px; font-size: 14px;
}
.mock-tiles { display: flex; gap: 10px; padding: 14px 16px; }
.mock-tile { display: flex; flex-direction: column; align-items: center; gap: 5px; font-size: 10px; color: var(--rc-ink-soft); font-weight: 600; }
.mock-tile-icon { width: 34px; height: 34px; border-radius: 10px; display: flex; align-items: center; justify-content: center; }
.mock-post { margin: 0 16px; background: #fff; border: 1px solid var(--rc-line); border-radius: 14px; padding: 12px; }
.mock-post-hdr { display: flex; align-items: center; gap: 8px; }
.mock-avatar { width: 26px; height: 26px; border-radius: 50%; background: linear-gradient(135deg, #C89B4C, #B3554A); }
.mock-post-name { font-size: 11.5px; font-weight: 700; color: var(--rc-ink); }
.mock-post-time { font-size: 9.5px; color: var(--rc-ink-soft); }
.mock-post-text { font-size: 10.5px; color: var(--rc-ink); margin: 8px 0; }
.mock-post-media { height: 80px; border-radius: 8px; background: linear-gradient(135deg, #E3ECE9, #C89B4C33); margin-bottom: 8px; }
.mock-post-actions { display: flex; gap: 14px; font-size: 10px; color: var(--rc-ink-soft); }

/* ==================== FEATURES ==================== */
.features-section { background: var(--rc-cream); }

/* ==================== PRIVATE BY DESIGN ==================== */
.privacy-section { background: #fff; }
.privacy-card { background: var(--rc-cream); border: 1px solid var(--rc-line); border-radius: 16px; padding: 24px; height: 100%; }
.privacy-icon { width: 46px; height: 46px; border-radius: 12px; background: #E3ECE9; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; }
.privacy-title { font-family: 'Fraunces', serif; font-weight: 500; font-size: 20px; color: var(--rc-ink); margin-bottom: 10px; }
.privacy-label { font-size: 11px; font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase; color: var(--rc-gold); margin-bottom: 4px; }
.privacy-who { font-size: 15px; font-weight: 600; color: var(--rc-ink); line-height: 1.5; margin-bottom: 8px; }
.privacy-note { font-size: 14px; color: var(--rc-ink-soft); line-height: 1.55; margin: 0; }
.privacy-strip { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px 28px; padding: 18px 24px; border-radius: 14px; background: var(--rc-cream); border: 1px solid var(--rc-line); }
.privacy-strip-item { display: flex; align-items: center; font-size: 14px; color: var(--rc-ink); font-weight: 500; }

/* ==================== TRIBZZ ==================== */
.tribzz-band { background: #1B1715; border-top: 4px solid #8E2B22; color: #F4EDE2; }
.tz-eyebrow { font-size: 12px; letter-spacing: 3px; color: rgba(244,237,226,0.65); margin-bottom: 14px; }
.tz-wordmark { font-family: 'Fraunces', Georgia, serif; font-weight: 600; font-size: 56px; letter-spacing: 6px; line-height: 1; color: #F4EDE2; }
.tz-tagline { font-size: 12px; letter-spacing: 3px; color: #D9A441; margin: 10px 0 26px; }
.tz-title { font-family: 'Fraunces', Georgia, serif; font-weight: 500; font-size: 30px; line-height: 1.25; color: #F4EDE2; margin-bottom: 16px; }
.tz-text { font-size: 16px; line-height: 1.7; color: rgba(244,237,226,0.82); margin-bottom: 26px; }
.tz-text strong { color: #F4EDE2; }
.tz-actions { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; }
.tz-btn.v-btn { background: #F4EDE2 !important; color: #1B1715 !important; font-weight: 700; text-transform: none; border-radius: 4px; letter-spacing: 0.2px; }
.tz-link.v-btn { color: #D9A441 !important; text-transform: none; font-weight: 600; }
.tz-card { background: #F4EDE2; color: #1B1715; border-radius: 4px; overflow: hidden; max-width: 420px; margin: 0 auto; box-shadow: 0 24px 60px rgba(0,0,0,0.45); }
.tz-card-cover { height: 150px; background: linear-gradient(135deg, #2F4F4A 0%, #1E3733 60%, #8E2B22 140%); display: flex; align-items: center; justify-content: center; }
.tz-card-letter { font-family: 'Fraunces', Georgia, serif; font-size: 72px; color: #F4EDE2; opacity: 0.9; }
.tz-card-body { padding: 20px 22px 22px; }
.tz-card-kicker { font-size: 11px; letter-spacing: 2.5px; color: #8E2B22; margin-bottom: 6px; }
.tz-card-name { font-family: 'Fraunces', Georgia, serif; font-size: 24px; font-weight: 600; margin-bottom: 4px; }
.tz-card-desc { font-family: 'Fraunces', Georgia, serif; font-style: italic; font-size: 15px; color: #4A403A; margin-bottom: 14px; }
.tz-card-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 14px; }
.tz-card-chips span { font-size: 10px; letter-spacing: 1.5px; border: 1px solid #1B1715; padding: 3px 8px; }
.tz-card-post { display: flex; flex-direction: column; gap: 6px; border-left: 2px solid #D9A441; padding-left: 10px; margin-bottom: 16px; }
.tz-quote { font-family: 'Fraunces', Georgia, serif; font-style: italic; font-size: 14px; }
.tz-here { font-size: 12px; color: #3F7A5C; font-weight: 600; }
.tz-card-join { background: #1B1715; color: #F4EDE2; text-align: center; padding: 10px; font-size: 12px; letter-spacing: 3px; font-weight: 700; }
.tz-card-caption { text-align: center; font-size: 12px; color: rgba(244,237,226,0.55); margin-top: 12px; }
.features-grid .feature-card { opacity: 0; transform: translateY(24px); transition: all 0.6s ease; }
.features-visible .feature-card { opacity: 1; transform: translateY(0); }
.feature-card {
  background: #fff; border: 1px solid var(--rc-line); border-radius: 18px; padding: 28px;
  height: 100%; transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.feature-card:hover { transform: translateY(-4px); box-shadow: 0 16px 32px rgba(30,55,51,0.08); }
.feature-icon { width: 52px; height: 52px; border-radius: 14px; display: flex; align-items: center; justify-content: center; margin-bottom: 18px; }
.feature-title { font-family: 'Fraunces', serif; font-weight: 500; font-size: 19px; color: var(--rc-ink); margin-bottom: 8px; }
.feature-desc { font-size: 14px; color: var(--rc-ink-soft); line-height: 1.6; margin-bottom: 14px; }
.feature-points { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 7px; }
.feature-points li { display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: var(--rc-ink); font-weight: 500; }

/* ==================== DEMO ==================== */
.demo-section { background: #fff; }
.demo-tabs { display: flex; justify-content: center; gap: 10px; flex-wrap: wrap; margin-bottom: 40px; }
.demo-tab {
  display: flex; align-items: center; gap: 8px; padding: 12px 22px; border-radius: 100px;
  border: 1.5px solid var(--rc-line); background: var(--rc-cream); font-weight: 600; font-size: 13.5px;
  color: var(--rc-ink-soft); cursor: pointer; transition: all 0.2s ease;
}
.demo-tab-active { background: var(--rc-teal); border-color: var(--rc-teal); color: var(--rc-cream) !important; }
.demo-tab-active .v-icon { color: var(--rc-gold-soft) !important; }

.demo-panel {
  max-width: 640px; margin: 0 auto; text-align: center;
  opacity: 0; transform: translateY(16px); transition: all 0.5s ease;
}
.demo-visible { opacity: 1; transform: translateY(0); }
.demo-panel-icon { width: 60px; height: 60px; border-radius: 16px; display: flex; align-items: center; justify-content: center; margin: 0 auto 18px; }
.demo-panel-title { font-family: 'Fraunces', serif; font-weight: 500; font-size: 26px; color: var(--rc-ink); margin-bottom: 10px; }
.demo-panel-desc { font-size: 15px; color: var(--rc-ink-soft); line-height: 1.7; margin-bottom: 22px; }
.demo-panel-items { display: flex; flex-direction: column; gap: 10px; align-items: flex-start; max-width: 420px; margin: 0 auto; }
.demo-panel-item { display: flex; align-items: center; gap: 8px; font-size: 14px; color: var(--rc-ink); font-weight: 500; }

.platform-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
.stat-card { background: var(--rc-cream); border: 1px solid var(--rc-line); border-radius: 14px; padding: 22px 10px; text-align: center; }
.stat-value { font-family: 'Fraunces', serif; font-weight: 500; font-size: 24px; color: var(--rc-teal); }
.stat-label { font-size: 12px; color: var(--rc-ink-soft); margin-top: 4px; }

/* ==================== BENEFITS ==================== */
.benefits-section { background: linear-gradient(160deg, var(--rc-teal-deep) 0%, var(--rc-teal) 100%); }
.benefits-visible .benefit-card { opacity: 1; transform: translateY(0); }
.benefit-card {
  display: flex; gap: 18px; background: rgba(251,247,239,0.06); border: 1px solid rgba(251,247,239,0.12);
  border-radius: 18px; padding: 26px; height: 100%; margin-bottom: 24px;
  opacity: 0; transform: translateY(20px); transition: all 0.6s ease;
}
.benefit-icon-section { width: 48px; height: 48px; border-radius: 14px; background: rgba(251,247,239,0.1); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.benefit-badge { font-size: 11px; font-weight: 700; letter-spacing: 0.12em; color: var(--rc-gold-soft); }
.benefit-title { font-family: 'Fraunces', serif; font-weight: 500; font-size: 20px; color: var(--rc-cream); margin: 6px 0 8px; }
.benefit-desc { font-size: 13.5px; color: rgba(251,247,239,0.7); line-height: 1.6; margin-bottom: 12px; }
.benefit-points { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 6px; }
.benefit-points li { display: flex; align-items: center; gap: 7px; font-size: 12.5px; color: rgba(251,247,239,0.85); }

/* ==================== TECHNOLOGY ==================== */
.technology-section { background: var(--rc-cream); }
.tech-card { text-align: center; padding: 16px 8px; }
.tech-title { font-size: 14px; font-weight: 700; color: var(--rc-ink); margin: 10px 0 4px; }
.tech-desc { font-size: 12px; color: var(--rc-ink-soft); line-height: 1.5; }

/* ==================== PRICING ==================== */
.pricing-section { background: #fff; }
.pricing-card {
  max-width: 980px; margin: 0 auto; background: var(--rc-cream); border: 1px solid var(--rc-line);
  border-radius: 24px; padding: 44px;
  opacity: 0; transform: translateY(20px); transition: all 0.6s ease;
}
.pricing-visible { opacity: 1; transform: translateY(0); }
.pricing-top { text-align: center; }
.pricing-price { font-family: 'Fraunces', serif; }
.price-num { font-size: 52px; font-weight: 600; color: var(--rc-teal); }
.price-period { font-size: 18px; color: var(--rc-ink-soft); }
.price-note { font-size: 13px; color: var(--rc-ink-soft); margin-top: 6px; }

.pricing-group { background: #fff; border: 1px solid var(--rc-line); border-radius: 14px; padding: 20px; height: 100%; margin-bottom: 16px; }
.pricing-group-hdr { display: flex; align-items: center; font-weight: 700; font-size: 14px; color: var(--rc-ink); margin-bottom: 14px; }
.pricing-feature { display: flex; align-items: flex-start; gap: 6px; margin-bottom: 10px; }
.pf-name { font-size: 13px; font-weight: 600; color: var(--rc-ink); }
.pf-detail { font-size: 11.5px; color: var(--rc-ink-soft); }

/* ==================== FINAL CTA ==================== */
.final-cta-section { background: linear-gradient(160deg, var(--rc-teal-deep) 0%, var(--rc-teal) 100%); }
.final-cta-title { font-family: 'Fraunces', serif; font-weight: 500; font-size: 32px; color: var(--rc-cream); }
.final-cta-subtitle { font-size: 15px; color: rgba(251,247,239,0.75); max-width: 480px; margin: 0 auto; }

/* Demo modal */
.demo-form-card { border-radius: 18px !important; }
.demo-form-header {
  background: linear-gradient(160deg, var(--rc-teal-deep) 0%, var(--rc-teal) 100%);
  color: var(--rc-cream); display: flex; align-items: center; justify-content: space-between; padding: 20px 24px;
}
.demo-form-title { display: flex; align-items: center; font-family: 'Fraunces', serif; font-weight: 500; font-size: 19px; }

@media (max-width: 960px) {
  .hero-title { font-size: 34px; }
  .platform-stats { grid-template-columns: repeat(2, 1fr); }
}
</style>